import { useState } from "react";
import { Target, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const areas = (profile) => [
  ...Object.entries(profile.sections).map(([k, v]) => ({ id: `section:${k}`, kind: "section", key: k, label: v.title, value: v.pct })),
  ...Object.entries(profile.intelligences).map(([k, v]) => ({ id: `intelligence:${k}`, kind: "intelligence", key: k, label: `${k} intelligence`, value: v })),
];

export const GoalsPanel = ({ token, profile, initialGoals }) => {
  const opts = areas(profile);
  const byId = Object.fromEntries(opts.map((o) => [o.id, o]));
  const [goals, setGoals] = useState(() => (initialGoals || []).map((g) => ({ id: `${g.kind}:${g.key}`, target: g.target })));
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const update = (next) => { setGoals(next); setDirty(true); };
  const add = () => {
    const free = opts.filter((o) => !goals.some((g) => g.id === o.id)).sort((a, b) => a.value - b.value)[0];
    if (free) update([...goals, { id: free.id, target: Math.min(100, Math.round(free.value) + 20) }]);
  };
  const save = async () => {
    setBusy(true);
    try {
      await http.put(`/results/${token}/goals`, { goals: goals.map((g) => ({ kind: byId[g.id].kind, key: byId[g.id].key, target: Number(g.target) })) });
      setDirty(false);
      toast.success("Goals saved. Retake the test later to see if you reached them.");
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save goals");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-[#1C242B]/10 p-6 sm:p-7" data-testid="goals-panel">
      <div className="flex items-center gap-2"><Target size={18} className="text-[#3E6B5C]" /><p className="overline-label">Growth goals</p></div>
      <p className="font-display text-xl font-semibold mt-3">Set up to 3 skill goals for your next test</p>
      <p className="text-sm text-slate-600 mt-1">Pick areas to practice and a target score. When you retake, we'll show whether you reached them.</p>
      <div className="mt-5 grid gap-3">
        {goals.map((g, i) => {
          const a = byId[g.id];
          return (
            <div key={i} className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_auto_auto_auto] items-center gap-2 sm:gap-3" data-testid={`goal-row-${i}`}>
              <select data-testid={`goal-area-select-${i}`} value={g.id} onChange={(e) => update(goals.map((x, j) => (j === i ? { ...x, id: e.target.value } : x)))}
                className="rounded-full border border-[#1C242B]/15 bg-[#FAF8F5] px-3 py-2 text-sm min-w-0">
                {opts.filter((o) => o.id === g.id || !goals.some((x) => x.id === o.id)).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <span className="hidden sm:inline font-mono-ui text-xs text-slate-500">now {Math.round(a.value)} →</span>
              <input data-testid={`goal-target-input-${i}`} type="number" min={1} max={100} value={g.target}
                onChange={(e) => update(goals.map((x, j) => (j === i ? { ...x, target: e.target.value } : x)))}
                className="w-20 rounded-full border border-[#1C242B]/15 px-3 py-2 text-sm" />
              <button data-testid={`goal-remove-btn-${i}`} onClick={() => update(goals.filter((_, j) => j !== i))} className="p-2 rounded-full hover:bg-[#F6E7DF] transition-colors" aria-label="Remove goal"><X size={16} /></button>
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        {goals.length < 3 && (
          <button data-testid="goal-add-btn" onClick={add} className="inline-flex items-center gap-1.5 rounded-full border border-[#1C242B]/15 px-4 py-2 text-sm hover:border-[#1C242B]/40 transition-colors">
            <Plus size={14} /> Add goal
          </button>
        )}
        {dirty && (
          <button data-testid="goals-save-btn" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-[#3E6B5C] text-white px-5 py-2 text-sm font-semibold hover:bg-[#2F5548] disabled:opacity-50 transition-colors">
            {busy && <Loader2 size={14} className="animate-spin" />} Save goals
          </button>
        )}
      </div>
    </div>
  );
};
